import { type FC, useCallback, useEffect, useState } from 'react';

import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { usePrioritizedAdvertisement } from '@/widgets/ad/hooks/use-prioritized-advertisements';
import { useConfetti } from '@/widgets/confetti/model';
import { ConfettiVariant } from '@/widgets/confetti/model/types';
import { MultiplePrizes } from '@/widgets/multiple-prizes';

import { PlaycoinAnimatedTab } from '@/features/playcoin-animated-tab/ui/PlaycoinAnimatedTab';

import { usePDSubscription } from '@/entities/market/hooks/usePDSubscription';
import { useUser, useUserActions } from '@/entities/user/model/user.store';
import {
  isClickerPlaycoinPrize,
  isClickerXpPrize,
  isPartnerPrize,
  isPlaycoinPrize,
} from '@/entities/wheel/helpers/predicates';
import { useIsWheelAdAvailable } from '@/entities/wheel/hooks/use-is-wheel-ad-available';
import { useSpinMode } from '@/entities/wheel/hooks/use-spin-mode';
import { useWheelActions, useWheelState } from '@/entities/wheel/model/wheel.store';
import { type Prize, WheelMode } from '@/entities/wheel/model/wheel.types';

import { Icon } from '@/shared/ui/icon';

import { ReactComponent as loader } from '@/assets/images/loader.svg?react';

import { usePrizeMainButton } from '../hooks/usePrizeMainButton';
import { CONFIG_BY_PRIZE_TYPE } from './constants';
import { PrizeImage } from './prize-image';
import { PrizeName } from './prize-name';

import s from './index.module.scss';

interface Props {
  className?: string;
}

const PrizePage: FC<Props> = ({ className }) => {
  const { t } = useTranslation();

  const user = useUser();
  const { pathname, state } = useLocation();

  const { footer } = state ?? {};

  const { fetchUser } = useUserActions();

  const { prizes = [], isActiveAd } = useWheelState();

  const { hydrate } = useWheelActions();
  const { isActivePDSubscription } = usePDSubscription();

  const [hidePrizeForAd, setHidePrizeForAd] = useState(
    isActiveAd && !isActivePDSubscription && !user?.access.noAds
  );
  const [hasErrorAd, setHasErrorAd] = useState(false);

  const mainPrize: Prize | undefined =
    prizes.find((prize) => isPartnerPrize(prize) || prize.isSuperPrize) ?? prizes[0];

  const isSuperPrize = !!mainPrize?.isSuperPrize;

  const [isTaken, setIsTaken] = useState(false);
  const [isShowingLootbox, setIsShowingLootbox] = useState(
    isSuperPrize && prizes.length === 1
  );

  const [isTabAnimating, setIsTabAnimating] = useState(false);
  const [isTabAnimationFinished, setIsTabAnimationFinished] = useState(false);

  const isWheelAdAvailable = useIsWheelAdAvailable();

  const [mode] = useSpinMode();

  const isX10SpinMode = mode === WheelMode.SPIN_10;

  const resetStore = useCallback(() => {
    hydrate({
      showReward: false,
      spinning: false,
      prizes: [],
      isActiveAd: isWheelAdAvailable,
    });
  }, [hydrate, isWheelAdAvailable]);

  const goBack = useCallback(() => {
    history.back();
    resetStore();
  }, [resetStore]);

  const { show: showAdvertisement } = usePrioritizedAdvertisement('wheel', {
    onReward: () => setHidePrizeForAd(false),
    onError: () => setHasErrorAd(true),
    onSkip: () => setHidePrizeForAd(false),
  });

  // Redirect to wheel page if there are no prizes
  useEffect(() => {
    if (!prizes.length) {
      goBack();
    } else {
      if (isClickerPlaycoinPrize(mainPrize) || isClickerXpPrize(mainPrize)) {
        void fetchUser();
      }

      if (hidePrizeForAd) showAdvertisement();
    }

    return () => {
      resetStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isClicker = !!mainPrize && !isShowingLootbox;

  const hasConfetti = !hidePrizeForAd && (prizes.length > 1 || isClicker);

  const showConfetti = useConfetti();

  useEffect(() => {
    if (hasConfetti) showConfetti(ConfettiVariant.screen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePrizeMainButton({
    prizes,
    mainPrize,
    isTaken,
    setIsTaken,
    setIsTabAnimating,
    pathname,
    isShowingLootbox,
    hidePrizeForAd,
    setHidePrizeForAd,
  });

  if (!prizes.length) return null;

  const getPageBackground = () => {
    if (hidePrizeForAd) return undefined;

    if (prizes.length > 1) return undefined;

    if (isPartnerPrize(mainPrize)) {
      return CONFIG_BY_PRIZE_TYPE.PARTNER.pageStyles;
    }

    return CONFIG_BY_PRIZE_TYPE[mainPrize.type]?.pageStyles;
  };

  return (
    <div
      className={clsx(s.container, hidePrizeForAd && s.ad, className)}
      style={getPageBackground()}
    >
      {hidePrizeForAd && (
        <>
          {hasErrorAd ? (
            <div className={s.error}>
              {t("Something went wrong, and we couldn't load the ad")}
            </div>
          ) : (
            <Icon icon={loader} className={s.loader} />
          )}
        </>
      )}
      {!hidePrizeForAd && (
        <>
          {prizes.length === 1 && isPlaycoinPrize(mainPrize) && (
            <PlaycoinAnimatedTab
              className={s['playcoin-tab']}
              amount={mainPrize.amount}
              animate={isTabAnimating}
              onRest={() => setIsTabAnimating(false)}
            />
          )}

          <div className={s.prizes}>
            {prizes.length === 1 && (
              <div className={s.prize}>
                <PrizeImage
                  prize={mainPrize}
                  prizeType={mainPrize.type}
                  partSize={mainPrize.partSize}
                  switchIcon={isTaken && !isTabAnimationFinished}
                  onComplete={() => setIsTabAnimationFinished(true)}
                  isLootbox={isShowingLootbox}
                  onLootboxOpened={() => setIsShowingLootbox(false)}
                />
                <PrizeName prize={mainPrize} isLootbox={isShowingLootbox} />
              </div>
            )}
            {prizes.length > 1 && (
              <MultiplePrizes
                title={
                  isPartnerPrize(mainPrize)
                    ? t('Wow! 3 prizes from', { count: prizes.length })
                    : t('You won 3 prizes!', {
                        count: isX10SpinMode ? 10 : prizes.length,
                      })
                }
                prizes={prizes}
              />
            )}
          </div>
        </>
      )}

      {!!footer && <div className={s.footer}>{footer}</div>}
    </div>
  );
};

export default PrizePage;
